import React, { useState, useEffect, useCallback } from 'react';
import { uploadsApi, outletsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, ArrowRight, CheckCircle, WarningCircle, XCircle,
  CaretDown, CaretUp, MagnifyingGlass, Receipt, CurrencyInr, Bank,
  Storefront, Tag, Scales, Lightning, ArrowClockwise, Trash, Eye,
  PencilSimple, FilePlus, X
} from '@phosphor-icons/react';

/* ─── mapping section definitions ─── */
const SECTION_FIELDS = {
  identity: {
    label: 'Order Identity', icon: Receipt, required: true,
    fields: [
      { key: 'invoice_number', label: 'Invoice Number', required: true },
      { key: 'business_date', label: 'Business Date', required: true },
      { key: 'invoice_date', label: 'Invoice Date' },
      { key: 'invoice_type', label: 'Invoice Type' },
      { key: 'source_order_number', label: 'Source Order Number' },
    ],
  },
  revenue: {
    label: 'Revenue', icon: CurrencyInr, required: true,
    fields: [
      { key: 'gross_amount', label: 'Gross Amount', required: true },
      { key: 'discounts', label: 'Discounts', required: true },
      { key: 'net_amount', label: 'Net Amount', required: true },
    ],
  },
  taxes: {
    label: 'Taxes', icon: Scales, required: true,
    fields: [{ key: 'taxes_total', label: 'Taxes Total', required: true }],
  },
  bill: {
    label: 'Final Bill', icon: Bank, required: true,
    fields: [
      { key: 'final_total', label: 'Total', required: true },
      { key: 'rounding', label: 'Rounding' },
      { key: 'tip', label: 'Tip' },
    ],
  },
  payments: {
    label: 'Payments', icon: Tag, required: true,
    fields: [
      { key: 'paid_amount', label: 'Paid Amount', required: true },
      { key: 'balance_due', label: 'Balance Due', required: true },
    ],
  },
  revenue_buckets: {
    label: 'Revenue Buckets', icon: Storefront, required: false, fields: [],
  },
  charges: {
    label: 'Charges', icon: Lightning, required: false, collapsed: true,
    fields: [
      { key: 'charges_total', label: 'Charges Total' },
      { key: 'charges_removed', label: 'Charges Removed' },
    ],
  },
  source_meta: {
    label: 'Source & Outlet', icon: MagnifyingGlass, required: false, collapsed: true,
    fields: [
      { key: 'order_source', label: 'Order Source' },
      { key: 'source_outlet_id', label: 'Source Outlet Id' },
      { key: 'channel', label: 'Channel' },
    ],
  },
};

/* ─── helpers ─── */
const fmtCurrency = (v) =>
  v != null ? `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '-';

const badgeClass = {
  UPLOADED:  'bg-blue-500/20 text-blue-400',
  MAPPED:    'bg-violet-500/20 text-violet-400',
  VALIDATED: 'bg-amber-500/20 text-amber-400',
  CONFIRMED: 'bg-emerald-500/20 text-emerald-400',
  FAILED:    'bg-red-500/20 text-red-400',
  DELETED:   'bg-zinc-500/20 text-zinc-500',
};

/* ─── Column Select ─── */
const ColSelect = ({ value, onChange, headers, label, required }) => (
  <div className="flex items-center gap-2">
    <Select value={value || '__NONE__'} onValueChange={(v) => onChange(v === '__NONE__' ? '' : v)}>
      <SelectTrigger data-testid={`map-${label}`} className="bg-[#18181B] border-white/10 h-8 text-xs font-mono flex-1">
        <SelectValue placeholder="-- select --" />
      </SelectTrigger>
      <SelectContent className="max-h-56">
        <SelectItem value="__NONE__">-- not mapped --</SelectItem>
        {headers.map((h) => (
          <SelectItem key={h} value={h}>{h}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <span className={`text-xs min-w-[110px] truncate ${required ? 'text-zinc-200' : 'text-zinc-500'}`}>
      {label} {required && <span className="text-[#D4FF00]">*</span>}
    </span>
  </div>
);

/* ───────────────────────────────────────────────────────────────────────── */
const UploadsPage = () => {
  const [uploads, setUploads] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState('');

  /* wizard state */
  const [wizard, setWizard] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dynPayments, setDynPayments] = useState({});
  const [dynCharges, setDynCharges] = useState({});
  const [dynAccounts, setDynAccounts] = useState({});
  const [dynTaxes, setDynTaxes] = useState([]);
  const [metadataCols, setMetadataCols] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({ charges: true, source_meta: true });

  /* validation / preview state */
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [valResult, setValResult] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  /* view data dialog */
  const [viewDataUpload, setViewDataUpload] = useState(null);
  const [viewDataRecords, setViewDataRecords] = useState([]);
  const [viewDataTotal, setViewDataTotal] = useState(0);
  const [viewDataLoading, setViewDataLoading] = useState(false);
  const [viewDataExpandedRow, setViewDataExpandedRow] = useState(null);

  /* action loading states */
  const [actionLoading, setActionLoading] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [uploadsRes, outletsRes] = await Promise.all([
        uploadsApi.list(),
        outletsApi.list(),
      ]);
      setUploads(uploadsRes.data);
      setOutlets(outletsRes.data);
    } catch (err) { toast.error('Failed to load uploads'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── upload handler ─── */
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedOutlet) { toast.error('Select an outlet before uploading'); return; }

    setUploading(true);
    try {
      const res = await uploadsApi.uploadSalesFile(file, 'TRANSACTION_LEVEL', selectedOutlet);
      const d = res.data;
      toast.success('File uploaded — mapping wizard opened');
      openWizardFromUploadResponse(d, file.name);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  /* Open wizard from fresh upload or resume */
  const openWizardFromUploadResponse = (d, fileName) => {
    const ad = d.auto_detected || {};
    const suggested = ad.suggested_mapping || {};
    setMapping(suggested);
    setDynPayments(ad.payment_columns || {});
    setDynCharges(ad.charge_columns || {});
    setDynAccounts(ad.account_columns || {});
    setDynTaxes(ad.tax_pairs || []);
    setMetadataCols(ad.metadata_columns || {});
    setValResult(null);
    setExpandedRow(null);
    setCollapsedSections({ charges: true, source_meta: true });
    setWizard({
      fileId: d.sales_uploaded_file_id || d.upload_id,
      fileName: fileName || d.file_name,
      headers: d.detected_headers || [],
      autoDetected: ad,
    });
  };

  /* ─── Continue Mapping (resume saved session) ─── */
  const handleContinueMapping = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'continue' }));
    try {
      const res = await uploadsApi.getDetail(upload.id);
      const detail = res.data;
      const u = detail.upload;
      const m = detail.mapping;
      const vs = u.validation_summary_json || {};
      const ad = vs.auto_detected || {};

      // If outlet is set on upload, select it
      if (u.outlet_id) setSelectedOutlet(u.outlet_id);

      if (m) {
        // Restore saved mapping
        setMapping(m.mapping_json || {});
        const dc = m.dynamic_columns_json || {};
        setDynPayments(dc.payments || {});
        setDynCharges(dc.charges || {});
        setDynAccounts(dc.accounts || {});
        setDynTaxes(dc.taxes || []);
        setMetadataCols(m.metadata_columns_json || {});
      } else {
        // No mapping saved yet — use auto-detected suggestions
        const suggested = ad.suggested_mapping || {};
        setMapping(suggested);
        setDynPayments(ad.payment_columns || {});
        setDynCharges(ad.charge_columns || {});
        setDynAccounts(ad.account_columns || {});
        setDynTaxes(ad.tax_pairs || []);
        setMetadataCols(ad.metadata_columns || {});
      }

      setValResult(null);
      setExpandedRow(null);
      setCollapsedSections({ charges: true, source_meta: true });
      setWizard({
        fileId: u.id,
        fileName: u.file_name,
        headers: vs.headers || [],
        autoDetected: ad,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load upload session');
    } finally {
      setActionLoading((s) => ({ ...s, [upload.id]: null }));
    }
  };

  /* ─── save mapping + validate ─── */
  const handleValidate = async () => {
    if (!wizard) return;
    setValidating(true);
    try {
      await uploadsApi.mapColumns(wizard.fileId, {
        source_granularity: 'ORDER_LEVEL',
        mapping,
        dynamic_columns: { payments: dynPayments, charges: dynCharges, accounts: dynAccounts, taxes: dynTaxes },
        metadata_columns: metadataCols,
        mapping_version: '1.0.0',
      });
      const res = await uploadsApi.validate(wizard.fileId);
      setValResult(res.data);
      toast.success('Validation complete');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Validation failed');
    } finally { setValidating(false); }
  };

  /* ─── confirm ingestion (from wizard) ─── */
  const handleConfirm = async () => {
    if (!wizard) return;
    setConfirming(true);
    try {
      const res = await uploadsApi.confirm(wizard.fileId);
      const d = res.data;
      toast.success(`Ingested ${d.records_ingested} canonical records`);
      setWizard(null);
      setValResult(null);
      fetchData();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired — please sign in again');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        toast.error('Request timed out — the file may be too large. Try again.');
      } else {
        toast.error(detail || 'Ingestion failed');
      }
    } finally { setConfirming(false); }
  };

  /* ─── Confirm from table row (for validated uploads) ─── */
  const handleConfirmFromTable = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'confirm' }));
    try {
      const res = await uploadsApi.confirm(upload.id);
      toast.success(`Confirmed — ${res.data.records_ingested} records ingested`);
      fetchData();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired — please sign in again');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        toast.error('Request timed out — the file may be too large. Try again.');
      } else {
        toast.error(detail || 'Confirmation failed');
      }
    } finally {
      setActionLoading((s) => ({ ...s, [upload.id]: null }));
    }
  };

  /* ─── Revalidate from table row ─── */
  const handleRevalidate = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'revalidate' }));
    try {
      await uploadsApi.revalidate(upload.id);
      toast.success('Re-validation complete');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-validation failed');
    } finally {
      setActionLoading((s) => ({ ...s, [upload.id]: null }));
    }
  };

  /* ─── Replace file ─── */
  const handleReplaceFile = (upload) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setActionLoading((s) => ({ ...s, [upload.id]: 'replace' }));
      try {
        await uploadsApi.replace(upload.id, file);
        toast.success('File replaced — mapping reset. Continue mapping to proceed.');
        fetchData();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Replace failed');
      } finally {
        setActionLoading((s) => ({ ...s, [upload.id]: null }));
      }
    };
    input.click();
  };

  /* ─── Governed Delete ─── */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteStep, setDeleteStep] = useState(null); // 'impact' | 'confirm'
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async (upload) => {
    setDeleteTarget(upload);
    setDeleteStep('impact');
    setDeleteConfirmText('');
    setDeleteReason('');
    setDeleteLoading(true);
    try {
      const res = await uploadsApi.deleteImpact(upload.id);
      setDeleteImpact(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load delete impact');
      setDeleteTarget(null);
      setDeleteStep(null);
    } finally { setDeleteLoading(false); }
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const body = {};
      if (deleteImpact?.is_confirmed) {
        body.confirm_text = deleteConfirmText;
        body.reason = deleteReason;
      }
      await uploadsApi.delete(deleteTarget.id, body);
      toast.success(`Upload "${deleteTarget.file_name}" deleted`);
      setDeleteTarget(null);
      setDeleteStep(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setDeleteLoading(false); }
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteStep(null);
    setDeleteImpact(null);
  };

  /* ─── View Data (for confirmed uploads) ─── */
  const handleViewData = async (upload) => {
    setViewDataUpload(upload);
    setViewDataLoading(true);
    setViewDataExpandedRow(null);
    try {
      const res = await uploadsApi.canonicalPreview(upload.id, 50);
      setViewDataRecords(res.data.records || []);
      setViewDataTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load canonical data');
      setViewDataRecords([]);
    } finally { setViewDataLoading(false); }
  };

  /* ─── update mapping helper ─── */
  const setField = (key, val) => setMapping((m) => ({ ...m, [key]: val }));

  /* ─── render ─── */
  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-zinc-400">Loading...</div></div>;

  const selectedOutletObj = outlets.find((o) => o.id === selectedOutlet);

  return (
    <div className="space-y-6" data-testid="uploads-page">
      {/* header */}
      <div>
        <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight">Sales Transaction Upload</h1>
        <p className="text-sm text-zinc-400 mt-1">Finance-grade order-level ingestion — canonical normalization for Accounts</p>
      </div>

      {/* upload card */}
      <div className="surface-base rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Outlet <span className="text-[#D4FF00]">*</span></Label>
            <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
              <SelectTrigger data-testid="outlet-select" className="bg-[#18181B] border-white/10">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.length === 0 && <SelectItem value="__none" disabled>No outlets — create one first</SelectItem>}
                {outlets.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.display_name || o.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <p className="text-xs text-zinc-500">Upload a transaction report (CSV / Excel). System auto-detects Payment-*, Charge-*, Account-*, and tax columns.</p>
          </div>
        </div>
        <div className="border-2 border-dashed border-[#D4FF00]/30 rounded-lg p-8 text-center bg-[#D4FF00]/5">
          <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" id="sales-upload" data-testid="sales-file-input" />
          <label htmlFor="sales-upload" className="cursor-pointer">
            <Upload size={48} className="mx-auto text-[#D4FF00] mb-4" />
            <p className="text-sm text-zinc-300">{uploading ? 'Uploading...' : 'Click to upload transaction report'}</p>
            <p className="text-xs text-zinc-500 mt-1">CSV, Excel — one row per invoice/order</p>
          </label>
        </div>
      </div>

      {/* ────── MAPPING WIZARD ────── */}
      <Dialog open={!!wizard} onOpenChange={(open) => { if (!open) { setWizard(null); setValResult(null); } }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-6xl max-h-[92vh] overflow-y-auto p-0" aria-describedby="mapping-wizard-desc">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight">
              Financial Mapping — <span className="font-mono text-sm text-[#D4FF00]">{wizard?.fileName}</span>
            </DialogTitle>
            {selectedOutletObj && (
              <p id="mapping-wizard-desc" className="text-xs text-zinc-400 mt-1">
                Outlet: <span className="font-mono text-zinc-300">{selectedOutletObj.display_name || selectedOutletObj.id}</span>
              </p>
            )}
          </DialogHeader>

          <div className="px-6 py-4 space-y-5">
            {Object.entries(SECTION_FIELDS).map(([sectionKey, sec]) => {
              const Icon = sec.icon;
              const isCollapsed = !!collapsedSections[sectionKey];
              const isCollapsible = !!sec.collapsed;
              return (
                <div key={sectionKey} className="rounded-md border border-white/5 overflow-hidden" data-testid={`section-${sectionKey}`}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors text-left"
                    onClick={() => isCollapsible && setCollapsedSections((c) => ({ ...c, [sectionKey]: !c[sectionKey] }))}
                  >
                    <Icon size={16} className="text-[#D4FF00]" />
                    <span className="text-sm font-medium flex-1">{sec.label}</span>
                    {sec.required && <span className="text-[10px] uppercase tracking-widest text-[#D4FF00]/70 mr-2">required</span>}
                    {isCollapsible && (isCollapsed ? <CaretDown size={14} className="text-zinc-500" /> : <CaretUp size={14} className="text-zinc-500" />)}
                  </button>
                  {(!isCollapsible || !isCollapsed) && (
                    <div className="px-4 pb-4 pt-2 space-y-2">
                      {sec.fields.map((f) => (
                        <ColSelect key={f.key} value={mapping[f.key]} onChange={(v) => setField(f.key, v)} headers={wizard?.headers || []} label={f.label} required={f.required} />
                      ))}
                      {sectionKey === 'taxes' && dynTaxes.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-detected tax pairs</p>
                          {dynTaxes.map((tp) => (
                            <div key={`${tp.label}-${tp.rate}`} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/40 rounded text-xs font-mono">
                              <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                              <span className="text-zinc-300">{tp.label} {tp.rate}%</span>
                              <span className="text-zinc-500">Value: {tp.value_col || '—'}</span>
                              <span className="text-zinc-500">Amount: {tp.amount_col}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sectionKey === 'payments' && Object.keys(dynPayments).length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-detected payment columns</p>
                          {Object.entries(dynPayments).map(([col, norm]) => (
                            <div key={col} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/40 rounded text-xs font-mono">
                              <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                              <span className="text-zinc-300">{col}</span>
                              <ArrowRight size={12} className="text-zinc-600" />
                              <span className="text-[#D4FF00]/80">{norm}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sectionKey === 'revenue_buckets' && Object.keys(dynAccounts).length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-detected account columns</p>
                          {Object.entries(dynAccounts).map(([col, norm]) => (
                            <div key={col} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/40 rounded text-xs font-mono">
                              <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                              <span className="text-zinc-300">{col}</span>
                              <ArrowRight size={12} className="text-zinc-600" />
                              <span className="text-[#D4FF00]/80">{norm}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sectionKey === 'revenue_buckets' && Object.keys(dynAccounts).length === 0 && (
                        <p className="text-xs text-zinc-500 italic">No Account-* columns detected. Revenue buckets will be empty.</p>
                      )}
                      {sectionKey === 'charges' && Object.keys(dynCharges).length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-detected charge columns</p>
                          {Object.entries(dynCharges).map(([col, norm]) => (
                            <div key={col} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/40 rounded text-xs font-mono">
                              <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                              <span className="text-zinc-300">{col}</span>
                              <ArrowRight size={12} className="text-zinc-600" />
                              <span className="text-[#D4FF00]/80">{norm}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {sectionKey === 'source_meta' && Object.keys(metadataCols).length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Auto-ingested metadata</p>
                          {Object.entries(metadataCols).map(([field, col]) => (
                            <div key={field} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/40 rounded text-xs font-mono">
                              <CheckCircle size={14} className="text-zinc-500 shrink-0" />
                              <span className="text-zinc-400">{col}</span>
                              <ArrowRight size={12} className="text-zinc-600" />
                              <span className="text-zinc-500">{field}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <Button onClick={handleValidate} disabled={validating} className="w-full bg-violet-500 hover:bg-violet-600" data-testid="validate-btn">
              {validating ? 'Validating...' : 'Save Mapping & Validate'}
            </Button>

            {/* ──── VALIDATION RESULT ──── */}
            {valResult && <ValidationResultPanel valResult={valResult} expandedRow={expandedRow} setExpandedRow={setExpandedRow} onConfirm={handleConfirm} confirming={confirming} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* ────── UPLOAD LIFECYCLE TABLE ────── */}
      <div className="surface-base rounded-lg p-6">
        <h3 className="text-lg font-medium font-['Outfit'] mb-4">Upload Sessions</h3>
        {uploads.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">No uploads yet. Upload a transaction report above.</div>
        ) : (
          <div className="space-y-3" data-testid="uploads-list">
            {uploads.map((u) => (
              <UploadRow
                key={u.id}
                upload={u}
                outlets={outlets}
                actionLoading={actionLoading[u.id]}
                onContinueMapping={() => handleContinueMapping(u)}
                onConfirm={() => handleConfirmFromTable(u)}
                onRevalidate={() => handleRevalidate(u)}
                onReplace={() => handleReplaceFile(u)}
                onDelete={() => handleDelete(u)}
                onViewData={() => handleViewData(u)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ────── VIEW DATA DIALOG ────── */}
      <Dialog open={!!viewDataUpload} onOpenChange={(open) => { if (!open) setViewDataUpload(null); }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-6xl max-h-[92vh] overflow-y-auto p-0" aria-describedby="view-data-desc">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight">
              Confirmed Data — <span className="font-mono text-sm text-emerald-400">{viewDataUpload?.file_name}</span>
            </DialogTitle>
            <p id="view-data-desc" className="text-xs text-zinc-400 mt-1">
              {viewDataTotal} canonical records | Confirmed {viewDataUpload?.confirmed_at ? new Date(viewDataUpload.confirmed_at).toLocaleString() : ''}
            </p>
          </DialogHeader>
          <div className="px-6 py-4">
            {viewDataLoading ? (
              <div className="py-12 text-center text-zinc-500">Loading canonical records...</div>
            ) : viewDataRecords.length === 0 ? (
              <div className="py-12 text-center text-zinc-500">No canonical records found.</div>
            ) : (
              <CanonicalTable records={viewDataRecords} expandedRow={viewDataExpandedRow} setExpandedRow={setViewDataExpandedRow} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ────── GOVERNED DELETE DIALOG ────── */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteModal(); }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-lg" aria-describedby="delete-impact-desc">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight flex items-center gap-2">
              <Trash size={18} className="text-red-400" /> Delete Upload
            </DialogTitle>
          </DialogHeader>
          <div id="delete-impact-desc">
            {deleteLoading && !deleteImpact ? (
              <div className="py-8 text-center text-zinc-500">Calculating impact...</div>
            ) : deleteStep === 'impact' && deleteImpact ? (
              <div className="space-y-4" data-testid="delete-impact-preview">
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">File</span>
                    <span className="font-mono text-zinc-200">{deleteImpact.file_name}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Type</span>
                    <span className="font-mono text-zinc-300">{deleteImpact.upload_type}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Status</span>
                    <span className={`font-mono ${deleteImpact.is_confirmed ? 'text-emerald-400' : 'text-zinc-400'}`}>{deleteImpact.status}</span>
                  </div>
                  {deleteImpact.outlet_name && (
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Outlet</span>
                      <span className="font-mono text-zinc-300">{deleteImpact.outlet_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Canonical Records</span>
                    <span className={`font-mono ${deleteImpact.canonical_record_count > 0 ? 'text-red-400' : 'text-zinc-400'}`}>{deleteImpact.canonical_record_count}</span>
                  </div>
                </div>

                {deleteImpact.is_confirmed && (
                  <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3" data-testid="delete-warning">
                    <p className="text-xs text-red-300 leading-relaxed">
                      This will permanently remove the uploaded file and all <strong>{deleteImpact.canonical_record_count}</strong> canonical sales records created from it. Dashboards, explorer totals, and downstream analytics will change.
                    </p>
                    {deleteImpact.downstream_warning && (
                      <p className="text-[10px] text-red-400/60 mt-1.5">{deleteImpact.downstream_warning}</p>
                    )}
                  </div>
                )}

                {!deleteImpact.is_confirmed && (
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    This upload has not been confirmed. Only the upload session, mapping, and preview artifacts will be removed.
                  </p>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" size="sm" onClick={closeDeleteModal} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="delete-cancel">
                    Cancel
                  </Button>
                  {deleteImpact.is_confirmed ? (
                    <Button variant="destructive" size="sm" onClick={() => setDeleteStep('confirm')} className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20" data-testid="delete-proceed">
                      Proceed to Confirmation
                    </Button>
                  ) : (
                    <Button variant="destructive" size="sm" onClick={executeDelete} disabled={deleteLoading} className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20" data-testid="delete-execute-unconfirmed">
                      {deleteLoading ? 'Deleting...' : 'Delete Upload'}
                    </Button>
                  )}
                </div>
              </div>
            ) : deleteStep === 'confirm' && deleteImpact ? (
              <div className="space-y-4" data-testid="delete-confirm-step">
                <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3">
                  <p className="text-xs text-red-300 font-medium">Destructive Action</p>
                  <p className="text-[11px] text-red-300/80 mt-1 leading-relaxed">
                    You are about to permanently delete <strong>{deleteImpact.file_name}</strong> and {deleteImpact.canonical_record_count} canonical records. This cannot be undone.
                  </p>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Type DELETE to confirm</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-zinc-200 outline-none focus:border-red-500/40 placeholder:text-zinc-700"
                    data-testid="delete-confirm-input"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Reason (optional)</label>
                  <input
                    type="text"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Why are you deleting this upload?"
                    className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2 text-xs text-zinc-200 outline-none focus:border-white/20 placeholder:text-zinc-700"
                    data-testid="delete-reason-input"
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" size="sm" onClick={() => setDeleteStep('impact')} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="delete-back">
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                    onClick={executeDelete}
                    className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 disabled:opacity-30"
                    data-testid="delete-execute-confirmed"
                  >
                    {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════ UPLOAD ROW COMPONENT ═══════ */
const UploadRow = ({ upload: u, outlets, actionLoading, onContinueMapping, onConfirm, onRevalidate, onReplace, onDelete, onViewData }) => {
  const vs = u.validation_summary_json || {};
  const fs = vs.financial_summary || {};
  const status = u.mapping_status;
  const outletObj = outlets.find((o) => o.id === u.outlet_id);
  const isLoading = !!actionLoading;

  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.01] hover:bg-white/[0.02] transition-colors" data-testid={`upload-row-${u.id}`}>
      {/* top info bar */}
      <div className="px-4 py-3 flex items-start gap-4 flex-wrap">
        {/* file info */}
        <div className="flex-1 min-w-[200px]">
          <p className="font-mono text-sm text-zinc-200 truncate" data-testid={`upload-filename-${u.id}`}>{u.file_name}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
            {outletObj && <span>{outletObj.display_name}</span>}
            <span>{new Date(u.uploaded_at).toLocaleDateString()}</span>
            {u.upload_type && <span className="font-mono">{u.upload_type}</span>}
          </div>
        </div>

        {/* status badge */}
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-1 rounded text-xs font-mono font-medium ${badgeClass[status] || 'bg-zinc-800 text-zinc-400'}`} data-testid={`upload-status-${u.id}`}>
            {status}
          </span>
        </div>

        {/* metrics */}
        {vs.rows_total && (
          <div className="flex items-center gap-4 text-xs">
            <div className="text-center">
              <p className="font-mono text-zinc-300">{vs.rows_total}</p>
              <p className="text-zinc-600 text-[10px]">rows</p>
            </div>
            {vs.avg_confidence != null && (
              <div className="text-center">
                <p className="font-mono text-[#D4FF00]">{vs.avg_confidence}%</p>
                <p className="text-zinc-600 text-[10px]">confidence</p>
              </div>
            )}
            {fs.net_sales != null && (
              <div className="text-center">
                <p className="font-mono text-zinc-300">{fmtCurrency(fs.net_sales)}</p>
                <p className="text-zinc-600 text-[10px]">net sales</p>
              </div>
            )}
          </div>
        )}
        {u.confirmed_record_count != null && (
          <div className="text-center text-xs">
            <p className="font-mono text-emerald-400">{u.confirmed_record_count}</p>
            <p className="text-zinc-600 text-[10px]">confirmed</p>
          </div>
        )}
      </div>

      {/* action bar */}
      <div className="px-4 py-2 border-t border-white/5 flex items-center gap-2 flex-wrap">
        {/* UPLOADED or MAPPED */}
        {(status === 'UPLOADED' || status === 'MAPPED') && (
          <>
            <ActionBtn icon={PencilSimple} label="Continue Mapping" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`continue-mapping-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`delete-upload-${u.id}`} />
          </>
        )}

        {/* VALIDATED */}
        {status === 'VALIDATED' && (
          <>
            <ActionBtn icon={CheckCircle} label="Confirm Ingestion" onClick={onConfirm} loading={actionLoading === 'confirm'} disabled={isLoading} variant="primary" testId={`confirm-upload-${u.id}`} />
            <ActionBtn icon={ArrowClockwise} label="Re-validate" onClick={onRevalidate} loading={actionLoading === 'revalidate'} disabled={isLoading} testId={`revalidate-upload-${u.id}`} />
            <ActionBtn icon={FilePlus} label="Replace File" onClick={onReplace} loading={actionLoading === 'replace'} disabled={isLoading} testId={`replace-upload-${u.id}`} />
            <ActionBtn icon={PencilSimple} label="Edit Mapping" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`edit-mapping-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`delete-upload-${u.id}`} />
          </>
        )}

        {/* CONFIRMED */}
        {status === 'CONFIRMED' && (
          <>
            <ActionBtn icon={Eye} label="View Data" onClick={onViewData} loading={actionLoading === 'view'} disabled={isLoading} variant="primary" testId={`view-data-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`delete-upload-${u.id}`} />
          </>
        )}

        {/* FAILED */}
        {status === 'FAILED' && (
          <>
            <ActionBtn icon={Eye} label="Inspect Error" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`inspect-error-${u.id}`} />
            <ActionBtn icon={FilePlus} label="Replace File" onClick={onReplace} loading={actionLoading === 'replace'} disabled={isLoading} testId={`replace-upload-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`delete-upload-${u.id}`} />
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════ ACTION BUTTON ═══════ */
const ActionBtn = ({ icon: Icon, label, onClick, loading, disabled, variant, testId }) => {
  const base = 'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-40';
  const styles = {
    default: 'bg-white/5 hover:bg-white/10 text-zinc-300',
    primary: 'bg-[#D4FF00]/10 hover:bg-[#D4FF00]/20 text-[#D4FF00]',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${styles[variant] || styles.default}`} data-testid={testId}>
      {loading ? <ArrowClockwise size={14} className="animate-spin" /> : <Icon size={14} />}
      {label}
    </button>
  );
};

/* ═══════ VALIDATION RESULT PANEL ═══════ */
const ValidationResultPanel = ({ valResult, expandedRow, setExpandedRow, onConfirm, confirming }) => (
  <div className="space-y-4 mt-2" data-testid="validation-result">
    <div className="grid grid-cols-5 gap-3">
      {[
        { label: 'Total', value: valResult.summary.rows_total, cls: '' },
        { label: 'Pass', value: valResult.summary.rows_pass, cls: 'text-emerald-400' },
        { label: 'Warn', value: valResult.summary.rows_warn, cls: 'text-amber-400' },
        { label: 'Fail', value: valResult.summary.rows_fail, cls: 'text-red-400' },
        { label: 'Confidence', value: `${valResult.summary.avg_confidence}%`, cls: 'text-[#D4FF00]' },
      ].map((s) => (
        <div key={s.label} className="text-center p-2.5 bg-zinc-800/50 rounded">
          <p className={`text-xl font-mono font-light ${s.cls}`}>{s.value}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-4 gap-3">
      {[
        { label: 'Gross Sales', val: valResult.financial_summary.gross_sales },
        { label: 'Net Sales', val: valResult.financial_summary.net_sales },
        { label: 'Taxes', val: valResult.financial_summary.taxes_total },
        { label: 'Final Total', val: valResult.financial_summary.final_total },
        { label: 'Discounts', val: valResult.financial_summary.discounts },
        { label: 'Charges Net', val: valResult.financial_summary.charges_net },
        { label: 'Paid', val: valResult.financial_summary.paid_amount },
        { label: 'Balance Due', val: valResult.financial_summary.balance_due },
      ].map((s) => (
        <div key={s.label} className="p-2.5 bg-zinc-800/50 rounded">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
          <p className="text-base font-mono font-light">{fmtCurrency(s.val)}</p>
        </div>
      ))}
    </div>
    {(valResult.total_issues.errors > 0 || valResult.total_issues.warnings > 0) && (
      <div className="p-3 rounded bg-zinc-800/30 border border-white/5 text-xs space-y-1">
        <p className="text-zinc-400 font-medium">Validation Issues</p>
        {valResult.total_issues.errors > 0 && <p className="text-red-400">{valResult.total_issues.errors} error(s)</p>}
        {valResult.total_issues.warnings > 0 && <p className="text-amber-400">{valResult.total_issues.warnings} warning(s)</p>}
      </div>
    )}
    {valResult.preview?.length > 0 && <CanonicalTable records={valResult.preview} expandedRow={expandedRow} setExpandedRow={setExpandedRow} />}
    <Button onClick={onConfirm} disabled={confirming} className="w-full bg-[#D4FF00] hover:bg-[#D4FF00]/80 text-black font-medium" data-testid="confirm-ingestion-btn">
      {confirming ? 'Ingesting...' : `Confirm Ingestion — ${valResult.summary.rows_total} records`}
    </Button>
  </div>
);

/* ═══════ CANONICAL TABLE (shared by wizard preview + view data) ═══════ */
const CanonicalTable = ({ records, expandedRow, setExpandedRow }) => (
  <div className="overflow-x-auto rounded border border-white/5">
    <table className="w-full text-xs" data-testid="canonical-table">
      <thead>
        <tr className="bg-zinc-800/50 text-zinc-400 text-left">
          <th className="p-2 font-mono">Order ID</th>
          <th className="p-2 font-mono">Biz Date</th>
          <th className="p-2 font-mono text-right">Gross</th>
          <th className="p-2 font-mono text-right">Disc</th>
          <th className="p-2 font-mono text-right">Net</th>
          <th className="p-2 font-mono text-right">Tax</th>
          <th className="p-2 font-mono text-right">Total</th>
          <th className="p-2 font-mono text-right">Paid</th>
          <th className="p-2 font-mono">Payments</th>
          <th className="p-2 font-mono">Rev Split</th>
          <th className="p-2 font-mono text-center">Status</th>
          <th className="p-2 font-mono text-right">Conf%</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <React.Fragment key={r.id || i}>
            <tr className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors" onClick={() => setExpandedRow(expandedRow === i ? null : i)} data-testid={`preview-row-${i}`}>
              <td className="p-2 font-mono text-zinc-300 max-w-[120px] truncate">{r.order_id}</td>
              <td className="p-2 font-mono text-zinc-400">{r.business_date}</td>
              <td className="p-2 font-mono text-right">{fmtCurrency(r.gross_sales)}</td>
              <td className="p-2 font-mono text-right text-zinc-500">{fmtCurrency(r.discounts)}</td>
              <td className="p-2 font-mono text-right">{fmtCurrency(r.net_sales)}</td>
              <td className="p-2 font-mono text-right text-zinc-400">{fmtCurrency(r.taxes_total)}</td>
              <td className="p-2 font-mono text-right font-medium">{fmtCurrency(r.final_total)}</td>
              <td className="p-2 font-mono text-right">{fmtCurrency(r.paid_amount)}</td>
              <td className="p-2 font-mono text-zinc-400 max-w-[100px] truncate">{r.payments?.map((p) => p.payment_mode).join(', ') || '-'}</td>
              <td className="p-2 font-mono text-zinc-400 max-w-[100px] truncate">{Object.entries(r.revenue_split || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${fmtCurrency(v)}`).join(' ') || '-'}</td>
              <td className="p-2 text-center">
                {r.validation_status === 'PASS' && <CheckCircle size={16} className="text-emerald-400 inline" />}
                {r.validation_status === 'WARN' && <WarningCircle size={16} className="text-amber-400 inline" />}
                {r.validation_status === 'FAIL' && <XCircle size={16} className="text-red-400 inline" />}
              </td>
              <td className="p-2 font-mono text-right text-[#D4FF00]">{r.confidence_score}%</td>
            </tr>
            {expandedRow === i && (
              <tr><td colSpan={12} className="p-0"><RowDetail record={r} /></td></tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─── expanded row detail ─── */
const RowDetail = ({ record }) => {
  const r = record;
  return (
    <div className="bg-zinc-900/60 p-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs border-t border-white/5">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Tax Breakdown</p>
        {r.tax_breakdown?.length > 0 ? r.tax_breakdown.map((t) => (
          <div key={`${t.tax_type}-${t.rate}`} className="font-mono text-zinc-300">{t.tax_type} {t.rate}% — {fmtCurrency(t.tax_amount)} <span className="text-zinc-600">(on {fmtCurrency(t.taxable_value)})</span></div>
        )) : <p className="text-zinc-600 italic">None</p>}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Payments</p>
        {r.payments?.length > 0 ? r.payments.map((p) => (
          <div key={p.payment_mode} className="font-mono text-zinc-300">{p.payment_mode}: {fmtCurrency(p.amount)}</div>
        )) : <p className="text-zinc-600 italic">None</p>}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Charges</p>
        <div className="font-mono text-zinc-400">Total: {fmtCurrency(r.charges_total)} | Removed: {fmtCurrency(r.charges_removed)} | Net: {fmtCurrency(r.charges_net)}</div>
        {r.charges?.length > 0 && r.charges.map((c) => (
          <div key={c.charge_type} className="font-mono text-zinc-300">{c.charge_type}: {fmtCurrency(c.amount)}</div>
        ))}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Metadata & Flags</p>
        {r.source_metadata && Object.entries(r.source_metadata).map(([k, v]) => (
          <div key={k} className="font-mono text-zinc-500">{k}: <span className="text-zinc-300">{v}</span></div>
        ))}
        <div className="font-mono text-zinc-500 mt-1">channel: <span className="text-zinc-300">{r.channel || '-'}</span></div>
        <div className="font-mono text-zinc-500">order_source: <span className="text-zinc-300">{r.order_source || '-'}</span></div>
        {r.flags && (
          <div className="font-mono text-zinc-500 mt-1">
            {Object.entries(r.flags).map(([k, v]) => <span key={k} className="mr-2">{k}: {v ? 'Y' : 'N'}</span>)}
          </div>
        )}
        {r.validation_issues?.length > 0 && (
          <div className="mt-2">
            <p className="text-[10px] uppercase tracking-widest text-amber-400/70 mb-0.5">Issues</p>
            {r.validation_issues.map((iss) => (
              <p key={`${iss.rule}-${iss.message}`} className={`font-mono ${iss.severity === 'ERROR' ? 'text-red-400' : 'text-amber-400'}`}>[{iss.rule}] {iss.message}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadsPage;
